import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { buildPeriodOptions, fmtPeriod } from "../lib/format";
import { exportNodeToPdf } from "../lib/pdf";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import AskBar from "../components/dashboard/AskBar";
import AutoInsight from "../components/dashboard/AutoInsight";
import OffPlanAlert from "../components/dashboard/OffPlanAlert";
import KpiCards from "../components/dashboard/KpiCards";
import KpiDrillDown from "../components/dashboard/KpiDrillDown";
import BridgeCard from "../components/dashboard/BridgeCard";
import Breakdowns from "../components/dashboard/Breakdowns";
import TrendChart from "../components/dashboard/TrendChart";
import LocationTable from "../components/dashboard/LocationTable";
import PnLStatement from "../components/dashboard/PnLStatement";
import { ChevronDown, Download } from "lucide-react";
import DefaultViewSelect from "../components/DefaultViewSelect";

function Select({ value, onChange, options, testid, label }) {
  return (
    <div className="relative">
      <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">{label}</label>
      <div className="relative">
        <select
          value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="appearance-none bg-brand-surface border border-brand-border hover:border-brand-borderStrong text-sm text-brand-ink rounded-full pl-4 pr-10 py-2.5 focus:outline-none focus:border-brand-primary transition-colors duration-200 cursor-pointer min-w-[200px]"
        >
          {options.map((o, i) => (
            <option key={`${o.value}-${i}`} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="h-4 w-4 text-brand-tertiary absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [period, setPeriod] = useState("");
  const [compare, setCompare] = useState("prior");
  const [summary, setSummary] = useState(null);
  const [budgetCmp, setBudgetCmp] = useState(null);
  const [trend, setTrend] = useState([]);
  const [locBreakdown, setLocBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const snapshotRef = useRef(null);

  const loadMeta = useCallback(async () => {
    const { data } = await api.get("/financials/meta");
    setMeta(data);
    setPeriod((prev) => prev || `${data.latest_period}|${data.latest_period}`);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMeta().catch(() => {}); }, [loadMeta]);

  // Apply the user's saved default date-range preference once meta + auth are ready.
  const prefApplied = useRef(false);
  useEffect(() => {
    if (prefApplied.current || !meta?.periods?.length || user === null) return;
    prefApplied.current = true;
    const pref = (user && user.preferences && user.preferences.default_period_range) || "latest";
    setPeriod(pref === "all"
      ? `${meta.periods[0]}|${meta.latest_period}`
      : `${meta.latest_period}|${meta.latest_period}`);
  }, [meta, user]);

  const [start, end] = period ? period.split("|") : [null, null];

  const loadData = useCallback(async () => {
    if (!start || !end) return;
    setLoading(true);
    try {
      const [sumRes, trendRes] = await Promise.all([
        api.get("/financials/summary", { params: { location, start, end, compare } }),
        api.get("/financials/trend", { params: { location } }),
      ]);
      setSummary(sumRes.data);
      setTrend(trendRes.data.trend);
      if (["admin", "regional"].includes(user?.role)) {
        api.get("/budget/kpis", { params: { location, start, end, by_location: location === "all" } }).then(({ data }) => setBudgetCmp(data)).catch(() => setBudgetCmp(null));
      }
      if (location === "all") {
        const lb = await api.get("/financials/locations-breakdown", { params: { start, end } });
        setLocBreakdown(lb.data.locations);
      } else {
        setLocBreakdown([]);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, start, end, compare, user?.role]);

  useEffect(() => { loadData(); }, [loadData]);

  if (user?.role === "community") return <Navigate to="/sales" replace />;

  if (!meta || !summary) {
    return (
      <div className="min-h-screen bg-brand-page relative z-10">
        <Header />
        <div className="flex items-center justify-center h-[70vh]">
          <div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" />
        </div>
      </div>
    );
  }

  const periodOptions = buildPeriodOptions(meta.periods, meta.latest_period);
  const locationOptions = meta.locations.map((l) => ({ value: l.name, label: l.label }));
  if (location !== "all" && !locationOptions.some((o) => o.value === location)) locationOptions.push({ value: location, label: `${location} (no financial data yet)` });
  const isRange = start !== end;
  const currentLoc = meta.locations.find((l) => l.name === location);
  const periodLabel = isRange ? `${fmtPeriod(start)} \u2013 ${fmtPeriod(end)}` : fmtPeriod(end);
  const compareOptions = [
    { value: "prior", label: "vs Prior Period" },
    { value: "yoy", label: "vs Same Period Last Year" },
    { value: "none", label: "No Comparison" },
  ];
  const compLabel = summary.comparison
    ? `Compared with ${summary.comparison.start === summary.comparison.end ? fmtPeriod(summary.comparison.end) : `${fmtPeriod(summary.comparison.start)} \u2013 ${fmtPeriod(summary.comparison.end)}`}`
    : compare !== "none" ? "No comparison data available" : null;
  const bm = budgetCmp?.months || [];
  const budgetNote = bm.length ? `Budget gap covers ${bm.length === 1 ? fmtPeriod(bm[0]) : `${fmtPeriod(bm[0])} \u2013 ${fmtPeriod(bm[bm.length - 1])}`}` : null;

  const handleDownload = async () => {
    setCapturing(true);
    await new Promise((r) => setTimeout(r, 120));
    try {
      const safe = (s) => (s || "view").replace(/[^a-z0-9]+/gi, "-");
      await exportNodeToPdf(snapshotRef.current, `BondCollective_${safe(currentLoc?.label)}_${safe(periodLabel)}.pdf`);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8"><AskBar /></div>
        {/* Title + filters */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary" data-testid="dashboard-context">
              {currentLoc?.label} · {periodLabel}
            </p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">
              Financial Performance
            </h1>
            {currentLoc?.city && (
              <p className="text-sm text-brand-muted mt-1">{currentLoc.city}</p>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <Select label="Location" testid="filter-location" value={location} onChange={setLocation} options={locationOptions} />
            <Select label="Time Period" testid="filter-period" value={period} onChange={setPeriod} options={periodOptions} />
            <div className="pb-2"><DefaultViewSelect /></div>
            <Select label="Compare" testid="filter-compare" value={compare} onChange={setCompare} options={compareOptions} />
            <button
              type="button"
              onClick={handleDownload}
              disabled={capturing}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-page text-sm rounded-full px-5 py-2.5 hover:bg-brand-ink/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
            >
              <Download className="h-4 w-4" /> {capturing ? "Preparing\u2026" : "Download PDF"}
            </button>
          </div>
        </div>

        <OffPlanAlert budget={budgetCmp} location={location} onOpen={location === "all" ? setLocation : undefined} />
        <div className="mb-6"><AutoInsight summary={summary} isRange={isRange} /></div>

        <motion.div
          key={`${location}-${period}-${compare}`}
          initial={{ opacity: 0 }} animate={{ opacity: loading ? 0.5 : 1 }} transition={{ duration: 0.25 }}
          className="space-y-6"
        >
          {(compLabel || budgetNote) && (
            <p className="text-xs text-brand-tertiary -mb-2" data-testid="comparison-label">{[compLabel, budgetNote].filter(Boolean).join(" · ")}</p>
          )}
          <div ref={snapshotRef} className="space-y-6 bg-brand-page">
            {capturing && (
              <div className="flex items-center justify-between border-b border-brand-border pb-4" data-testid="pdf-header">
                <div>
                  <p className="font-serif text-2xl text-brand-ink">Bond Collective</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary mt-1">Financial Snapshot</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-brand-ink">{currentLoc?.label}</p>
                  <p className="text-xs text-brand-muted">{periodLabel}</p>
                  <p className="text-[10px] text-brand-tertiary mt-1">
                    Generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              </div>
            )}
            <KpiCards totals={summary.totals} isRange={isRange} comparison={summary.comparison} budget={budgetCmp} onDrill={setDrill} trend={trend} />
            <BridgeCard totals={summary.totals} consolidated={summary.consolidated} />

            {location === "all" && locBreakdown.length > 0 && (
              <LocationTable locations={locBreakdown} onSelect={setLocation} />
            )}
          </div>

          <TrendChart data={trend} />
          <Breakdowns summary={summary} />
          <PnLStatement summary={summary} consolidated={summary.consolidated} />
        </motion.div>
      </main>

      <KpiDrillDown metric={drill} summary={summary} locBreakdown={locBreakdown} periodLabel={periodLabel} onClose={() => setDrill(null)} />
    </div>
  );
}
